import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { SendIcon } from '@modernloop/shared/icons';
import { Box, CircularProgress, IconButton, Stack, TextField, Typography, textFieldClasses } from '@mui/material';

import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

import TimelineMessage from './TimelineMessage';
import { useAiSchedulingStream } from './useAiSchedulingStream';

const NODE_SERVICE_URL = 'http://localhost:8787';
const DEV_AUTH_TOKEN = 'Bearer dev@dev.modernloop.xyz';

type AiTimelineProps = {
  taskId: string;
  orgId: string;
  actorEmployeeId: string;
  applicationId: string;
};

const AiTimeline: React.FC<AiTimelineProps> = ({ taskId, orgId, actorEmployeeId, applicationId }) => {
  const { t } = useTranslation();
  const aiTaskEnabled = useIsAiTaskEnabled();

  const { messages, isStreaming, sendMessage } = useAiSchedulingStream({
    nodeServiceUrl: NODE_SERVICE_URL,
    orgId,
    taskId,
    actorEmployeeId,
    applicationId,
    authToken: DEV_AUTH_TOKEN,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');

  // Auto-scroll to bottom on new messages
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes browser or analytics APIs after render.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || isStreaming) {
      return;
    }
    sendMessage(text);
    setInputValue('');
  }, [inputValue, isStreaming, sendMessage]);

  if (!aiTaskEnabled) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack height="100%" direction="column">
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={2.5}
        py={1.5}
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {t('hiring-dashboard-tasks:tasks.aiTimeline.timeline')}
        </Typography>
      </Stack>

      {/* Messages */}
      <Stack
        ref={scrollRef}
        spacing={2}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 2.5,
          py: 2,
        }}
      >
        {messages.length === 0 && !isStreaming && (
          <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body2" color="text.secondary">
              {t('hiring-dashboard-tasks:tasks.aiTimeline.emptyState')}
            </Typography>
          </Box>
        )}

        {messages.map((message) => (
          <TimelineMessage key={message.id} message={message} />
        ))}
      </Stack>

      {/* Input */}
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.background.contrast,
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 2,
        }}
      >
        <TextField
          autoFocus
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={isStreaming}
          fullWidth
          placeholder={t('hiring-dashboard-tasks:tasks.aiTimeline.inputPlaceholder')}
          maxRows={5}
          sx={{
            [`&.${textFieldClasses.root} textarea`]: {
              padding: '5px 4px',
            },
          }}
          InputProps={{
            inputProps: { onKeyDown: handleKeyDown },
            endAdornment: isStreaming ? (
              <CircularProgress size={16} />
            ) : (
              <IconButton onClick={handleSend} disabled={!inputValue.trim()}>
                <SendIcon />
              </IconButton>
            ),
            sx: { borderRadius: '6px' },
            multiline: true,
          }}
        />
      </Box>
    </Stack>
  );
};

export default AiTimeline;
